import { useEffect, useRef } from "react";
import { loadAppleScript } from "../../user-auth/utils/apple-script-loader.ts";
import { useLoginConfig } from "../../user-auth/utils/login-config-context.tsx";
import { socialLogin } from "../../user-auth/utils/social-login.ts";
import { SocialButtonsProps } from "./social-buttons-props.ts";
import { Button } from "../buttons/button.tsx";

export const AppleButton: React.FC<SocialButtonsProps> = ({ onAuthSuccess }) => {
  const loginConfig = useLoginConfig();
  // ref to hold the promise that loads the apple script
  const appleScriptLoadingPromise = useRef<Promise<void> | null>(null);

  useEffect(() => {
    async function loadScript() {
      if (!loginConfig) return;
      try {
        appleScriptLoadingPromise.current = loadAppleScript();
        await appleScriptLoadingPromise.current;
      } catch (error) {
        console.error("Error loading Apple script", error);
      }

      window.AppleID.auth.init({
        clientId: loginConfig.appleClientId,
        scope: "email name",
        redirectURI: loginConfig.appleRedirectUri,
        state: "",
        usePopup: true,
      });
    }

    void loadScript();
  }, []);

  const handleClick = async () => {
    if (appleScriptLoadingPromise.current === null) {
      throw new Error("Apple script didn't start loading. Did you forget to call loadAppleScript?");
    }

    if (typeof window.AppleID !== "undefined") {
      try {
        const response = await window.AppleID.auth.signIn();
        //It only shares user's name and (optionally) email the first time around
        const requestBody = {
          token: response.authorization.id_token,
          scope: "Apple",
        };
        const requestOptions = {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        };
        const responseStatus = await socialLogin(requestOptions, "apple");
        await onAuthSuccess?.(responseStatus);
      } catch {
        throw new Error("Apple authorisation failed");
      }
    }
  };

  return (
    <Button
      onClick={() => void handleClick()}
      variant="solid"
      colorVariant="light"
      label="Apple"
      labelStyle="hidden md:block"
      renderLeadingIcon={() => (
        <img className="h-[18px]" src="/static/icons/apple-social-icon.png" alt="Apple social icon" />
      )}
      className="w-full"
    />
  );
};
